import { useState, useRef, MouseEvent, useMemo, useCallback, memo } from 'react'
import { Button, ButtonGroup as MuiButtonGroup } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from 'redux/Hook'
import OptionWithTooltip, {
  shouldShowTooltip,
} from 'components/Shared/OptionWithTooltip'
import ButtonGroupMenu from './ButtonGroupMenu'
import { FilterSetStyles } from './StyleConstants'
import { DEFAULT_CHAR_LIMIT, isMobileView } from 'lib/GlobalConstants'

const ButtonGroup = ({
  handleMenuItemClick,
  handleRenameSelection,
  handleDeleteSelection,
  selectedIndex = -1,
  filterSetResult,
  disabled = false,
  labelKey = 'reporting.filterSet.savedFilter',
}: {
  filterSetResult?: any
  handleMenuItemClick?: (event: any, index: number) => void
  handleRenameSelection?: (
    filterId: string,
    filterName: string,
    inputRef: any,
  ) => void
  handleDeleteSelection?: (event: any, index: number) => void
  testProps?: false
  selectedIndex?: number
  disabled?: boolean
  labelKey?: string
}) => {
  const [open, setOpen] = useState({ isOpen: false })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { user: { user: { adminLocale: adminLocale = '' } = {} } = {} } =
    useAppSelector((state) => state)

  const handleMenuClick = async (
    event: MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    handleMenuItemClick?.(event, index)
    setOpen({ isOpen: false })
  }

  const handleToggle = () => {
    setOpen((prevOpen) => ({ isOpen: !prevOpen.isOpen }))
    anchorRef?.current?.blur()
  }

  const handleClose = (event: Event) => {
    if (anchorRef?.current?.contains(event.target as HTMLElement)) return

    setOpen({ isOpen: false })

    if (buttonRef?.current) {
      buttonRef.current.focus()
    }
  }

  const longText = useCallback(
    ({
      filterName,
      typoGraphySxProps,
    }: {
      filterName: string
      typoGraphySxProps?: any
    }) => {
      return (
        <>
          {shouldShowTooltip(filterName, DEFAULT_CHAR_LIMIT) ? (
            <OptionWithTooltip
              option={filterName}
              charLimit={DEFAULT_CHAR_LIMIT}
              noWrap={false}
              typoGraphySx={typoGraphySxProps}
              componentPropsSx={{
                maxWidth: isMobileView ? anchorRef?.current?.clientWidth : 300,
                marginLeft: '0rem',
                marginTop: '2px',
              }}
              placement={'bottom'}
            />
          ) : (
            filterName
          )}
        </>
      )
    },
    [],
  )

  const optionsLength = filterSetResult?.length || 0 // onload should be 0
  const savedFilterText = useMemo(
    () => `${t(labelKey)} (${optionsLength})`,
    [filterSetResult, adminLocale],
  )

  const filterState =
    selectedIndex !== -1
      ? filterSetResult?.[selectedIndex]?.filterName &&
        longText({
          filterName: filterSetResult?.[selectedIndex]?.filterName,
          typoGraphySxProps: FilterSetStyles()?.['longTextStyle'],
        })
      : savedFilterText
  const btnText = !optionsLength ? savedFilterText : filterState
  const options = useMemo(() => filterSetResult, [])

  const buttonGroupMenuProps = {
    options,
    open,
    anchorRef,
    handleClose,
    selectedIndex,
    handleMenuClick,
    handleRenameSelection,
    handleDeleteSelection,
    longText,
  }
  const disabledState = useMemo(
    () => !optionsLength || disabled,
    [optionsLength, disabled],
  )
  return (
    <>
      <MuiButtonGroup
        ref={anchorRef}
        aria-label=" button"
        data-testid="filter-button-group"
        sx={FilterSetStyles({ isMobileView })?.['butttonGroup']}
        disabled={disabledState}
      >
        <Button
          onClick={handleToggle}
          sx={FilterSetStyles({ isMobileView, disabledState })?.savedFilter}
          ref={buttonRef}
        >
          {btnText}
        </Button>

        <Button
          size="large"
          aria-label="select filters"
          data-testid="filter-button-group-dropdown"
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={FilterSetStyles({ disabledState })?.['selectFilters']}
        >
          <ArrowDropDownIcon />
        </Button>
      </MuiButtonGroup>
      <ButtonGroupMenu {...buttonGroupMenuProps} />
    </>
  )
}

export default memo(ButtonGroup)
